import appConfig from '../../config/appConfig';
import * as errorPageTypes from '../../consts/app/errorPageTypes';
import queryParameters from '../../consts/route/queryParameters';
import * as routePaths from '../../consts/route/routePaths';
import * as routeValues from '../../consts/route/routeValues';
import * as advanceDiagnosticScreenTypes from '../../consts/yap/advanceDiagnosticScreenTypes';
import {setErrorPageType} from '../../state/ducks/global';
import {dispatch} from '../../state/store';
import helpers from '../../utils/helpers';
import urlUtils from '../../utils/urlUtils';
import routerService from '../routerService';
import httpStatusTypesService from '../server/httpStatusTypesService';

const forwardToAgeGatePage = () => routerService.forwardTo(routePaths.AGE_GATE);
const forwardToAgeVerificationFailedPage = () => routerService.forwardTo(routePaths.AGE_VERIFICATION_FAILED);
const forwardToAppIntroPage = () => routerService.forwardTo(routePaths.APP_INTRO);
const forwardToWelcomePwaPage = () => routerService.forwardTo(routePaths.WELCOME_PWA_PAGE);
const forwardToImportantPage = () => routerService.forwardTo(routePaths.IMPORTANT);
const forwardToBrowserVerificationPage = () => routerService.forwardTo(routePaths.BROWSER_VERIFICATION);
const forwardToConnectionGuidePage = ({deviceType = '', isNew}) =>
    routerService.forwardTo(
        urlUtils.join(routePaths.CONNECTION_GUIDE, deviceType, isNew ? routeValues.BLE_CONNECTION_NEW : '')
    );
const forwardToDeviceActivationPage = () => routerService.forwardTo(routePaths.ACTIVATION);
const forwardToDeviceSyncPage = () => routerService.forwardTo(routePaths.SYNC);
const forwardToDeviceRegistrationPage = () => routerService.forwardTo(routePaths.REGISTRATION);
const forwardToLoginPage = () => routerService.forwardTo(routePaths.LOGIN);
const forwardToMyDevicesPage = () => {
    const isHomePageEnabled = appConfig.getIsHomePageEnabled();

    isHomePageEnabled ? forwardToHomePage() : routerService.forwardTo(routePaths.MY_DEVICES);
};
const forwardToHomePage = () => routerService.forwardTo(routePaths.HOME_PAGE);
const forwardToNoDevicesPage = () => routerService.forwardTo(routePaths.NO_DEVICES);
const forwardToErrorHandlingPage = (errorType) => {
    dispatch(setErrorPageType(errorType));
    routerService.forwardTo(routePaths.ERROR_PAGE);
};
const forwardToIccErrorHandlingPage = (status) => {
    //check error status for icc maintenance
    const errorType = httpStatusTypesService.isServiceUnavailable(status)
        ? errorPageTypes.ICC_UNAVAILABLE_ERROR
        : errorPageTypes.ICC_TEMPORARY_ERROR;

    forwardToErrorHandlingPage(errorType);
};
const forwardToNotCompatibleBrowserPage = () => routerService.forwardTo(routePaths.NOT_COMPATIBLE_BROWSER);
const forwardToPairingAlertPage = ({deviceType = '', isNew}) =>
    routerService.forwardTo(
        urlUtils.join(routePaths.PAIRING_ALERT, deviceType, isNew ? routeValues.BLE_CONNECTION_NEW : '')
    );
const forwardToPairingGuidePage = ({deviceType = '', isNew, redirectToAfterConnect = '', retryNumber}) =>
    routerService.forwardTo(
        urlUtils.join(
            routePaths.PAIRING_GUIDE,
            deviceType,
            isNew ? routeValues.BLE_CONNECTION_NEW : redirectToAfterConnect ? '_' : '',
            redirectToAfterConnect,
            '?' + urlUtils.stringify({[queryParameters.RETRY_NUMBER]: retryNumber})
        )
    );
const forwardToPairingSuccessPage = () => routerService.forwardTo(routePaths.PAIRING_SUCCESS);
const forwardToQuickStartGuidePage = (deviceType = '') =>
    routerService.forwardTo(urlUtils.join(routePaths.QUICK_START_GUIDE, deviceType));
const forwardToRoot = () => routerService.forwardTo(routePaths.ROOT);
const forwardToSelectDevice = () => routerService.forwardTo(routePaths.SELECT_DEVICE);
const forwardToSelectMarketPage = () => routerService.forwardTo(routePaths.SELECT_MARKET);
const forwardToTipsAndTricksPage = () => routerService.forwardTo(routePaths.TIPS_AND_TRICKS);
const forwardToTNCDataCollectionPage = () => routerService.replace(routePaths.TNC_DATA_COLLECTION);
const forwardToTNCPreferenceCenterPage = () => routerService.forwardTo(routePaths.TNC_DATA_PREFERENCE);
const forwardToTNCPreferenceCenterSettingsPage = () => routerService.forwardTo(routePaths.TNC_DATA_PREFERENCE_SETTINGS);
const forwardToTNCTermsOfUsePage = () => routerService.replace(routePaths.TNC_TERMS_OF_USE);
const forwardToTutorialList = () => routerService.forwardTo(routePaths.TUTORIAL_LIST);
const forwardToTipsAndTutorialsList = (deviceType = '') =>
    routerService.forwardTo(urlUtils.join(routePaths.TIPS_AND_TUTORIALS, deviceType));
const forwardToCleaningGuide = ({isAdvancedDiagnostic} = {}) =>
    routerService.forwardTo(urlUtils.join(routePaths.CLEAN_TUTORIAL, '?' + urlUtils.stringify({isAdvancedDiagnostic})));
const forwardToWelcomeUserPage = () => routerService.forwardTo(routePaths.WELCOME_USER);
const forwardToLegalDisclaimerPage = () => routerService.forwardTo(routePaths.LEGAL_DISCLAIMER);
const forwardToBenefitPage = () => routerService.forwardTo(routePaths.BENEFIT_PAGE);
const forwardToUpdateFWPage = ({target, version = helpers.guid(), isSblFirmware = false}) =>
    routerService.forwardTo(
        urlUtils.join(routePaths.UPDATE, '?' + urlUtils.stringify({target, version, isSblFirmware}))
    );
const forwardToUpToDatePage = (successFwuCount = 1) =>
    routerService.forwardTo(urlUtils.join(routePaths.UP_TO_DATE, successFwuCount.toString()));
const forwardToUpToDateFailedPage = () => routerService.forwardTo(routePaths.UP_TO_DATE_FAILED);
const forwardToConnectionSelectionPage = ({deviceType = '', isNew}) =>
    routerService.forwardTo(
        urlUtils.join(routePaths.CONNECTION_SELECTION, deviceType, isNew ? routeValues.BLE_CONNECTION_NEW : '')
    );
const forwardToUpdateCheckingPage = () => routerService.forwardTo(routePaths.UPDATE_CHECKING);
const forwardToUpdateSummaryPage = () => routerService.forwardTo(routePaths.UPDATE_SUMMARY);

if (process.env.REACT_APP_ENV !== 'prod') {
    window.forwardTo = routerService.forwardTo;
}

/* ------------ Device Settings -------- */
const forwardToDeviceSettingsPage = () => routerService.forwardTo(routePaths.DEVICE_SETTINGS);
const forwardToAboutDevicePage = () => routerService.forwardTo(routePaths.ABOUT_DEVICE);
const forwardToAemFlexBatteryPage = () => routerService.forwardTo(routePaths.AEM_FLEX_BATTERY);
const forwardToAemPauseModePage = () => routerService.forwardTo(routePaths.AEM_PAUSE_MODE);
const forwardToFlexPuffPage = () => routerService.forwardTo(routePaths.FLEX_PUFF);
const forwardToAutoStartModePage = () => routerService.forwardTo(routePaths.AUTO_START_MODE);
const forwardToCleaningModePage = () => routerService.forwardTo(routePaths.CLEANING_MODE);
const forwardToConnectCarePage = () => routerService.forwardTo(routePaths.CONNECT_CARE);
const forwardToDeviceRenamePage = () => routerService.forwardTo(routePaths.DEVICE_RENAME);
const forwardToGestureModePage = () => routerService.forwardTo(routePaths.GESTURE_MODE);
const forwardToGetFeatureSectionPage = (getFeatureType) =>
    routerService.forwardTo(urlUtils.join(routePaths.GET_FEATURE_SECTION, getFeatureType || ''));
const forwardToLedModePage = () => routerService.forwardTo(routePaths.LED_MODE);
const forwardToExtendedLedModePage = () => routerService.forwardTo(routePaths.EXTENDED_LED_MODE);
const forwardToLocateMyDevicePage = () => routerService.forwardTo(routePaths.LOCATE_DEVICE);
const forwardToResponsiveDrawModePage = () => routerService.forwardTo(routePaths.RESPONSIVE_DRAW_MODE);
const forwardToRestartMyDeviceModePage = () => routerService.forwardTo(routePaths.RESTART_MY_DEVICE);
const forwardToResetDevicePage = () => routerService.forwardTo(routePaths.RESET_DEVICE);
const forwardToUpdateAvailablePage = () => routerService.forwardTo(routePaths.UPDATE_AVAILABLE);
const forwardToUsageStatsPage = () => routerService.forwardTo(routePaths.USAGE_STATS);
const forwardToVapeCloudSizeModePage = () => routerService.forwardTo(routePaths.VAPE_CLOUD_SIZE_MODE);
const forwardToVapeSessionModePage = () => routerService.forwardTo(routePaths.VAPE_SESSION_MODE);
const forwardToVibrationModePage = () => routerService.forwardTo(routePaths.VIBRATION_MODE);
const forwardToAdvancedDiagnosticScreen = ({screenName}) =>
    routerService.forwardTo(
        urlUtils.join(routePaths.ADVANCED_DIAGNOSTIC, '?' + urlUtils.stringify({screen: screenName}))
    );

const forwardToFirstPhaseAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.PHASE_1_SCREEN});
const forwardToSecondPhaseAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.PHASE_2_SCREEN});
const forwardToConsentAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.CONSENT_SCREEN});
const forwardToRunningAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.RUNNING_SCREEN});
const forwardToRunningFailAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.RUNNING_FAIL_SCREEN});
const forwardToDamageCheckAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.DAMAGE_CHECK_SCREEN});
const forwardToFittingConsumablesCheckAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.FITTING_CONSUMABLES_CHECK_SCREEN});
const forwardToFittingConsumablesResolvedAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.FITTING_CONSUMABLES_SOLVED});
const forwardToSelectIssueAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.SELECT_ISSUE_SCREEN});
const forwardToHolderDetectedAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.HOLDER_DETECTED_SCREEN});
const forwardToPostCleaningAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.POST_CLEANING_SCREEN});
const forwardToChargingIssueScreen = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.CHARGING_ISSUE_SCREEN});
const forwardToCleaningContactsScreenAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.CLEANING_CONTACTS_SCREEN});
const forwardToFwuScreenAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.FWU_SCREEN});
const forwardToFwuCheckScreenAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.FWU_CHECK_SCREEN});
const forwardToMarketScreenAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.MARKET_SCREEN});
const forwardToMultipleIssuesScreenAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.MULTIPLE_ISSUES_SCREEN});
const forwardToFinishScreenAD = () =>
    forwardToAdvancedDiagnosticScreen({screenName: advanceDiagnosticScreenTypes.FINISH_SCREEN});
/* ------------ END Device Settings -------- */

const getIsErrorPage = () => urlUtils.isPathnameIncludes(routePaths.ERROR_PAGE);

if (process.env.REACT_APP_ENV !== 'prod') {
    window.forwardTo = routerService.forwardTo;
}

export default {
    forwardToAboutDevicePage,
    forwardToAemFlexBatteryPage,
    forwardToAemPauseModePage,
    forwardToFlexPuffPage,
    forwardToAutoStartModePage,
    forwardToCleaningModePage,
    forwardToConnectCarePage,
    forwardToDeviceRenamePage,
    forwardToGestureModePage,
    forwardToGetFeatureSectionPage,
    forwardToLedModePage,
    forwardToExtendedLedModePage,
    forwardToLocateMyDevicePage,
    forwardToResponsiveDrawModePage,
    forwardToRestartMyDeviceModePage,
    forwardToUpdateAvailablePage,
    forwardToUsageStatsPage,
    forwardToVapeCloudSizeModePage,
    forwardToVapeSessionModePage,
    forwardToVibrationModePage,
    forwardToFinishScreenAD,
    forwardToFwuScreenAD,
    forwardToFwuCheckScreenAD,
    forwardToMarketScreenAD,
    forwardToMultipleIssuesScreenAD,
    forwardToFittingConsumablesCheckAD,
    forwardToFittingConsumablesResolvedAD,
    forwardToAdvancedDiagnosticScreen,
    forwardToFirstPhaseAD,
    forwardToSecondPhaseAD,
    forwardToConsentAD,
    forwardToDamageCheckAD,
    forwardToSelectIssueAD,
    forwardToRunningAD,
    forwardToRunningFailAD,
    forwardToHolderDetectedAD,
    forwardToPostCleaningAD,
    forwardToChargingIssueScreen,
    forwardToCleaningContactsScreenAD,
    forwardToAgeGatePage,
    forwardToAgeVerificationFailedPage,
    forwardToAppIntroPage,
    forwardToImportantPage,
    forwardToBenefitPage,
    forwardToBrowserVerificationPage,
    forwardToConnectionGuidePage,
    forwardToConnectionSelectionPage,
    forwardToDeviceActivationPage,
    forwardToDeviceRegistrationPage,
    forwardToDeviceSettingsPage,
    forwardToDeviceSyncPage,
    forwardToErrorHandlingPage,
    forwardToIccErrorHandlingPage,
    forwardToLegalDisclaimerPage,
    forwardToLoginPage,
    forwardToMyDevicesPage,
    forwardToHomePage,
    forwardToNoDevicesPage,
    forwardToNotCompatibleBrowserPage,
    forwardToPairingAlertPage,
    forwardToPairingGuidePage,
    forwardToPairingSuccessPage,
    forwardToQuickStartGuidePage,
    forwardToRoot,
    forwardToSelectDevice,
    forwardToSelectMarketPage,
    forwardToTipsAndTricksPage,
    forwardToTipsAndTutorialsList,
    forwardToTNCDataCollectionPage,
    forwardToTNCPreferenceCenterPage,
    forwardToTNCPreferenceCenterSettingsPage,
    forwardToTNCTermsOfUsePage,
    forwardToTutorialList,
    forwardToUpdateCheckingPage,
    forwardToUpdateFWPage,
    forwardToUpdateSummaryPage,
    forwardToUpToDateFailedPage,
    forwardToUpToDatePage,
    forwardToWelcomePwaPage,
    forwardToWelcomeUserPage,
    forwardToResetDevicePage,
    forwardToCleaningGuide,
    getIsErrorPage,
};
